import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building2,
  Shield,
  Briefcase,
  Pill,
  FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';

const PendingContributions = () => {
  const navigate = useNavigate();
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [reviewModal, setReviewModal] = useState({ open: false, contribution: null, action: null });
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchContributions = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { contribution_type: filter } : {};
      const response = await api.get('/platform/pending-contributions', { params });
      setContributions(response.data.contributions);
    } catch (error) {
      toast.error('Failed to load contributions');
    } finally {
      setLoading(false);
    }
  }, [filter]);


  useEffect(() => {
    fetchContributions();
  }, [filter, fetchContributions]);
  const handleReview = async () => {
    const { contribution, action } = reviewModal;
    
    try {
      if (action === 'approve') {
        await api.post(`/platform/approve-contribution/${contribution.id}`, {
          notes: reviewNotes
        });
        toast.success(`${contribution.type.charAt(0).toUpperCase() + contribution.type.slice(1)} approved and added system-wide!`);
      } else {
        await api.post(`/platform/reject-contribution/${contribution.id}`, {
          notes: reviewNotes
        });
        toast.success('Contribution rejected');
      }
      
      setReviewModal({ open: false, contribution: null, action: null });
      setReviewNotes('');
      fetchContributions();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to process contribution'));
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'role':
        return <Shield className="w-5 h-5 text-purple-600" />;
      case 'department':
        return <Building2 className="w-5 h-5 text-blue-600" />;
      case 'medication':
        return <Pill className="w-5 h-5 text-green-600" />;
      case 'lab_test':
        return <FlaskConical className="w-5 h-5 text-amber-600" />;
      default:
        return <Briefcase className="w-5 h-5 text-slate-600" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      role: 'bg-purple-100 text-purple-800',
      department: 'bg-blue-100 text-blue-800',
      medication: 'bg-green-100 text-green-800',
      lab_test: 'bg-amber-100 text-amber-800'
    };
    return colors[type] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div data-testid="pending-contributions-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/platform')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Pending Contributions</h1>
            <p className="text-slate-500">Review and approve institutional contributions</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-10 px-3 rounded-md border text-sm"
            >
              <option value="all">All Types</option>
              <option value="role">Roles</option>
              <option value="department">Departments</option>
              <option value="medication">Medications</option>
              <option value="lab_test">Lab Tests</option>
            </select>
          </div>
        </div>
      </header>

      <div className="page-content max-w-6xl">
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : contributions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No pending contributions</p>
              <p className="text-sm mt-2">Institutional contributions will appear here for review</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contributions.map((contrib) => (
              <Card key={contrib.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {getTypeIcon(contrib.type)}
                        <div>
                          <h3 className="font-semibold text-lg">{contrib.data.name}</h3>
                          <p className="text-sm text-slate-500">
                            Submitted by {contrib.submitted_by_name} from {contrib.institution_name}
                          </p>
                        </div>
                        <Badge className={getTypeBadge(contrib.type)}>
                          {contrib.type}
                        </Badge>
                      </div>
                      
                      {contrib.data.description && (
                        <p className="text-sm text-slate-600 mb-3">{contrib.data.description}</p>
                      )}
                      
                      {contrib.data.generic_name && (
                        <p className="text-sm text-slate-600">
                          <strong>Generic Name:</strong> {contrib.data.generic_name}
                        </p>
                      )}
                      
                      {contrib.data.category && (
                        <p className="text-sm text-slate-600">
                          <strong>Category:</strong> {contrib.data.category}
                        </p>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">
                        Submitted: {new Date(contrib.submitted_at).toLocaleString()}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => setReviewModal({ open: true, contribution: contrib, action: 'approve' })}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReviewModal({ open: true, contribution: contrib, action: 'reject' })}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => setReviewModal({ open, contribution: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewModal.action === 'approve' ? 'Approve' : 'Reject'} Contribution
            </DialogTitle>
          </DialogHeader>
          
          {reviewModal.contribution && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{reviewModal.contribution.data.name}</p>
                <p className="text-sm text-slate-600">Type: {reviewModal.contribution.type}</p>
                <p className="text-sm text-slate-600">From: {reviewModal.contribution.institution_name}</p>
              </div>
              
              <div>
                <Label>Review Notes {reviewModal.action === 'reject' ? '(Required)' : '(Optional)'}</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={reviewModal.action === 'approve' ? 'Add notes (optional)' : 'Reason for rejection'}
                  rows={3}
                />
              </div>
              
              {reviewModal.action === 'approve' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✅ This will make the {reviewModal.contribution.type} available to ALL institutions system-wide.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, contribution: null, action: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              className={reviewModal.action === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {reviewModal.action === 'approve' ? 'Approve & Publish' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingContributions;
